/**
 * AdminAgentDemandTab — V143.1.
 *
 * Admin-only view of `/api/agent-waitlist/demand` — shows the demand
 * ranking for the 12 "Pripravujeme & testujeme" agents. This is the
 * P0 dev backlog: the agent with the most signups ships next.
 *
 * Layout: total count chip + DESC ranking table with rank / agent /
 * signups / "→ Build next" callout for top row.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, RefreshCw, Trophy } from "lucide-react";
import { API } from "../../config";
import { comingSoonAgents } from "../../data/agentCatalog";

export default function AdminAgentDemandTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const SOON = comingSoonAgents();
  const slugToAgent = Object.fromEntries(SOON.map((a) => [a.slug, a]));

  const fetchData = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/agent-waitlist/demand`);
      setData(r.data);
    } catch (e) {
      setErr("Demand ranking nedostupný");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Merge: every coming-soon agent appears in the table, even with 0
  // signups, so admin sees the full picture.
  const rows = (() => {
    if (!data) return [];
    const bySlug = Object.fromEntries((data.rows || []).map((r) => [r.agent_slug, r.signups]));
    return SOON
      .map((a) => ({ ...a, signups: bySlug[a.slug] || 0 }))
      .sort((x, y) => y.signups - x.signups);
  })();

  return (
    <div data-testid="admin-agent-demand-panel" className="space-y-6">
      {/* Header + total */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-2">
            <Flame className="w-6 h-6 text-amber-500" />
            Agent Demand Ranking
          </h2>
          <p className="text-sm text-gray-500">
            Waitlist signups pre 12 "Pripravujeme & testujeme" agentov. Najviac demanded → ďalší v dev pipeline.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-amber-50 border border-amber-200 px-4 py-2">
            <p className="text-[10px] uppercase tracking-widest font-black text-amber-600">Total signups</p>
            <p className="text-3xl font-mono font-black text-amber-700" data-testid="admin-demand-total">{data?.total ?? "—"}</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            data-testid="admin-demand-refresh"
            className="bg-gray-900 text-white p-2 hover:bg-black disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {err ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 p-3">{err}</p>
      ) : null}

      {/* V144.1 — Threshold + email alert + Stripe mode banner */}
      <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="admin-demand-info-banner">
        <span className="font-bold">🔥 Pre-order threshold:</span>
        <span>25 signupov · €29 deposit</span>
        <span className="text-blue-400">·</span>
        <span className="font-bold">📧 Email alert:</span>
        <span>Po prekročení 25 signupov ti príde mail (1× per agent)</span>
        <span className="text-blue-400">·</span>
        <span className="font-bold">💳 Stripe mode:</span>
        {data?.stripe_test_mode === true ? (
          <span className="bg-emerald-100 text-emerald-800 font-black px-2 py-0.5 border border-emerald-300" data-testid="stripe-mode-badge">
            🧪 SANDBOX (žiadne € transakcie)
          </span>
        ) : data?.stripe_test_mode === false ? (
          <span className="bg-red-100 text-red-800 font-black px-2 py-0.5 border border-red-300" data-testid="stripe-mode-badge">
            🔴 LIVE (REAL € TRANSACTIONS)
          </span>
        ) : (
          <span className="text-gray-500">načítavam…</span>
        )}
      </div>

      {/* Ranking table */}
      <div className="border border-gray-200 overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest font-black text-gray-500 w-12">#</th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest font-black text-gray-500">Agent</th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest font-black text-gray-500">ETA</th>
              <th className="px-4 py-3 text-right text-[10px] uppercase tracking-widest font-black text-gray-500">Cena</th>
              <th className="px-4 py-3 text-right text-[10px] uppercase tracking-widest font-black text-amber-600">Signups</th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-widest font-black text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">Žiadne dáta</td></tr>
            ) : null}
            {rows.map((a, i) => (
              <tr
                key={a.slug}
                data-testid={`admin-demand-row-${a.slug}`}
                className={`border-t border-gray-100 ${i === 0 && a.signups > 0 ? "bg-amber-50" : "hover:bg-gray-50"}`}
              >
                <td className="px-4 py-3">
                  {i === 0 && a.signups > 0 ? (
                    <Trophy className="w-4 h-4 text-amber-500" />
                  ) : (
                    <span className="text-gray-400 font-mono">{i + 1}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2">
                    <span className="text-xl">{a.emoji}</span>
                    <span className="font-bold text-gray-900">{a.name}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 font-mono">{a.eta}</td>
                <td className="px-4 py-3 text-right text-gray-700 font-mono">€{a.price_eur_month}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-black text-lg ${a.signups > 0 ? "text-amber-600" : "text-gray-300"}`}>
                    {a.signups}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {a.signups >= 25 ? (
                    <span className="inline-flex items-center gap-1 bg-emerald-500 text-black font-black uppercase tracking-widest px-2 py-1 text-[10px]">
                      🔥 Pre-order open
                    </span>
                  ) : i === 0 && a.signups > 0 ? (
                    <span className="inline-flex items-center gap-1 bg-amber-500 text-black font-black uppercase tracking-widest px-2 py-1 text-[10px]">
                      → Build next
                    </span>
                  ) : a.signups >= 5 ? (
                    <span className="text-emerald-600 font-bold">Hot</span>
                  ) : a.signups >= 1 ? (
                    <span className="text-blue-600 font-bold">Validated</span>
                  ) : (
                    <span className="text-gray-400">Žiadny dopyt</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed">
        Dáta z <code className="bg-gray-100 px-1.5 py-0.5">GET /api/agent-waitlist/demand</code> · cache 0 (real-time). Public funnel (`/prenajmi-si-agenta`) cache-uje 5 min cez `window.__demandCache`.
      </p>
    </div>
  );
}
